// The widget formats every app shares (VisionPlatform specs/widget-fixtures WFX-06, P-113). The fixtures' expected
// texts are produced by these rules, and VisionPlatform's checker recomputes them, so keep this file in step with
// tools/schema-check/fixtures.go there.
import type { HistorySeries } from "./generated/widget-fixture";

const NBSP = String.fromCharCode(0xa0);

/** The point's decimals and the locale's separators; a no-break space before the unit, none before the angle sign. */
export function formatValue(value: number, unit: string | null, decimals: number, locale: string): string {
  const q = 10 ** decimals;
  const zero = Math.round(Math.abs(value) * q) === 0;
  const n = new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(
    zero ? 0 : value,
  );
  if (!unit) return n;
  return unit === "°" ? `${n}°` : n + NBSP + unit;
}

export type AgeText = { key: "age.justNow" } | { key: "age.minutes" | "age.hours"; count: number } | { date: Date };

/** "just now" under a minute, then whole minutes and whole hours; the date after a day. */
export function valueAge(at: Date, now: Date): AgeText {
  const s = (now.getTime() - at.getTime()) / 1000;
  if (s < 60) return { key: "age.justNow" };
  if (s < 3600) return { key: "age.minutes", count: Math.floor(s / 60) };
  if (s < 86400) return { key: "age.hours", count: Math.floor(s / 3600) };
  return { date: at };
}

/** The latest value against the first point of the last hour; less than one unit of the last decimal is flat. */
export function trend(series: HistorySeries | undefined, now: Date, latest: number, decimals: number) {
  if (!series) return undefined;
  const step = 10 ** -decimals;
  for (const p of series.points) {
    const t = typeof p[0] === "string" ? Date.parse(p[0]) : NaN;
    if (Number.isNaN(t) || now.getTime() - t > 3_600_000) continue;
    const first = p.length === 4 ? p[2] : p[1];
    if (typeof first !== "number") continue;
    const d = latest - first;
    return d >= step ? "up" : d <= -step ? "down" : "flat";
  }
  return "flat";
}
